import React, { Fragment, useContext, useEffect, useState } from 'react';
import { useMatch } from 'react-router-dom';
import { GlobalContext, handleError } from '../../../context/Provider';
import henceforthApi from '../../../utils/henceforthApi';
import BreadCrumb from '../../../components/common/BreadCrumb';
import Spinner from '../../../components/common/spinner';
import { toast } from 'react-toastify';
import commonArray from '../../../components/common/commonArray';
import { Button } from '../../../components/ui/button';
import { Boxes, Search, X, AlertCircle, Save, Check } from 'lucide-react';

const AddVariant: React.FC = () => {
  const match = useMatch('product/:id/variant/add');
  const { language_data, loading, setLoading, authState, toastMessage, authDispatch } =
    useContext(GlobalContext);

  const breadCrumbPath = [
    {
      name: `${language_data?.[authState.lang]?.common_home || 'Home'}`,
      url: `/`,
      active: '',
    },
    {
      name: `${language_data?.[authState.lang]?.common_product || 'Product'} ${language_data?.[authState.lang]?.common_list || 'List'
        }`,
      url: `/products/1`,
      active: '',
    },
    {
      name: `${language_data?.[authState.lang]?.common_product || 'Product'} ${language_data?.[authState.lang]?.common_deatil || 'Detail'
        }`,
      url: `/product/${match?.params.id}`,
      active: '',
    },
    {
      name: `${language_data?.[authState.lang]?.common_product || 'Product'} ${language_data?.[authState.lang]?.common_variant || 'Variant'
        }`,
      url: ``,
      active: 'not-allowed',
    },
  ];

  const [searchProduct, setSearchProduct] = useState<string>('');
  const [productList, setProductList] = useState<{ _id: string; name: string }[]>([]);
  const [variant, setVariant] = useState<Array<{ _id: string; name: string }>>([]);
  const [variantErr, setVariantErr] = useState<string>('');
  const [load, setLoad] = useState<boolean>(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!variant.length) {
      return setVariantErr(
        `${language_data?.[authState.lang]?.common_please || 'Please'} ${language_data?.[authState.lang]?.common_add || 'add'
        } ${language_data?.[authState.lang]?.common_one || 'one'} ${language_data?.[authState.lang]?.common_variant || 'variant'
        } ${language_data?.[authState.lang]?.common_for || 'for'} ${language_data?.[authState.lang]?.common_linking || 'linking'
        }`
      );
    }
    const items = {
      product_id_1: match?.params.id,
      product_id_2: variant[0]._id,
      language: 'ENGLISH',
    };

    setLoading(true);
    try {
      const res = await henceforthApi.Product.addProductDetailsDynamically(
        commonArray.productOtherDetails[2],
        items
      );
      toastMessage(res.message || 'Variant linked successfully');
      window.history.back();
    } catch (err) {
      handleError(err, 'active', authDispatch);
    } finally {
      setLoading(false);
    }
  };

  const productListing = async () => {
    setLoad(true);
    try {
      const res = (await henceforthApi.Product.getProductList(searchProduct, '', '', '')).data.data;
      setProductList(res || []);
    } catch (err) {
      console.log('err', err);
      handleError(err, '', authDispatch);
    } finally {
      setLoad(false);
    }
  };

  useEffect(() => {
    productListing();
  }, [searchProduct]);

  const product_idsHandler = (id: string, name: string, index: number) => {
    if (id && name) {
      if (variant[0]?._id) {
        return toast.warn(
          `${language_data?.[authState.lang]?.common_you_have_selected || 'You have selected'} ${language_data?.[authState.lang]?.common_one || 'one'
          } ${language_data?.[authState.lang]?.common_product || 'product'} ${language_data?.[authState.lang]?.common_already_ || 'already'
          }`
        );
      }
      const temp2 = variant.some((res) => res._id === id && res.name === name);
      if (temp2) {
        return toast.warn(
          `${language_data?.[authState.lang]?.common_already_ || 'Already'} ${language_data?.[authState.lang]?.common_added_ || 'added'
          }`
        );
      }
      setVariant([...variant, { _id: id, name: name }]);
      setSearchProduct('');
      setVariantErr('');
    } else {
      const updated = [...variant];
      updated.splice(index, 1);
      setVariant(updated);
    }
  };

  return (
    <Fragment>
      <BreadCrumb pathNameDeclare={breadCrumbPath} />

      <div className="w-full px-4 sm:px-6 lg:px-8 py-4 space-y-4">
        <div className="bg-white rounded-2xl border border-slate-200/80 shadow-xs p-4 sm:p-4 space-y-4">
          {/* Header */}
          <div className="flex items-center gap-3.5 pb-4 border-b border-slate-100">
            <div className="w-10 h-10 rounded-xl bg-sky-50 text-[#1AA7DE] flex items-center justify-center shrink-0 shadow-2xs">
              <Boxes className="w-5 h-5" />
            </div>
            <div>
              <h2 className="text-base sm:text-lg font-bold text-slate-900 tracking-tight m-0">
                {language_data?.[authState.lang]?.common_add || 'Add'}{' '}
                {language_data?.[authState.lang]?.common_variant || 'Variant Link'}
              </h2>
              <p className="text-xs text-slate-500 font-medium m-0 mt-0.5">
                {language_data?.[authState.lang]?.common_only_one_variant_can_be_added ||
                  'Link another product as a variant'}
              </p>
            </div>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-2">
              <label className="block text-xs sm:text-sm font-bold text-slate-700 tracking-wide">
                {language_data?.[authState.lang]?.common_search || 'Search'}{' '}
                {language_data?.[authState.lang]?.common_product || 'Product'}{' '}
                <span className="text-slate-400 font-normal">
                  ({language_data?.[authState.lang]?.common_only_one_variant_can_be_added || '1 variant max'})
                </span>
              </label>

              {/* Search input with relative dropdown */}
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-slate-400">
                  <Search className="w-4 h-4" />
                </div>
                <input
                  type="search"
                  name="search"
                  value={searchProduct}
                  disabled={variant.length >= 1}
                  onChange={(e) => {
                    setLoad(true);
                    setSearchProduct(e.target.value);
                    setVariantErr('');
                  }}
                  placeholder={`${language_data?.[authState.lang]?.common_search || 'Search'} ${language_data?.[authState.lang]?.common_your || 'your'
                    } ${language_data?.[authState.lang]?.common_product || 'product'}...`}
                  className={`w-full pl-10 pr-4 py-2.5 rounded-xl border text-xs sm:text-sm font-medium transition-all focus:outline-none focus:ring-2 disabled:bg-slate-100 disabled:text-slate-400 ${variantErr
                      ? 'border-rose-300 bg-rose-50/30 text-rose-900 focus:ring-rose-200 focus:border-rose-500'
                      : 'border-slate-200 bg-slate-50/50 hover:bg-white focus:bg-white text-slate-800 focus:ring-[#1AA7DE]/20 focus:border-[#1AA7DE]'
                    }`}
                />

                {/* Dropdown search results */}
                {searchProduct && Array.isArray(productList) && !load && productList.length > 0 && (
                  <div className="absolute z-20 left-0 right-0 mt-1.5 max-h-56 overflow-y-auto bg-white rounded-xl border border-slate-200 shadow-lg divide-y divide-slate-100">
                    {productList
                      .filter((res) => res._id !== match?.params.id)
                      .map((res) => (
                        <button
                          key={res._id}
                          type="button"
                          onClick={() => product_idsHandler(res._id, res.name, -1)}
                          className="w-full text-left px-4 py-2.5 hover:bg-sky-50/60 transition-colors flex items-center justify-between group"
                        >
                          <span className="text-xs sm:text-sm font-medium text-slate-700 group-hover:text-slate-900">
                            {res.name}
                          </span>
                          <span className="text-2xs text-[#1AA7DE] font-semibold opacity-0 group-hover:opacity-100 transition-opacity">
                            Select
                          </span>
                        </button>
                      ))}
                  </div>
                )}
              </div>

              {/* Error message */}
              {variantErr && (
                <div className="flex items-center gap-1.5 text-xs font-semibold text-rose-600 pt-1">
                  <AlertCircle className="w-3.5 h-3.5 shrink-0" />
                  <span>{variantErr}</span>
                </div>
              )}

              {/* Selected Variant Chip */}
              {Array.isArray(variant) && variant.length > 0 && (
                <div className="pt-2">
                  <span className="text-2xs uppercase tracking-wider text-slate-400 font-bold block mb-1.5">
                    Selected Product:
                  </span>
                  <div className="flex flex-wrap gap-2">
                    {variant.map((res, index) => (
                      <div
                        key={res._id}
                        className="inline-flex items-center gap-2 px-3 py-1.5 rounded-xl bg-sky-50 border border-sky-100 text-[#1AA7DE] text-xs font-semibold shadow-2xs"
                      >
                        <Check className="w-3.5 h-3.5" />
                        <span className="text-slate-800 font-medium">{res.name}</span>
                        <button
                          type="button"
                          onClick={() => product_idsHandler('', '', index)}
                          className="w-4 h-4 rounded-full flex items-center justify-center text-slate-400 hover:text-rose-500 hover:bg-rose-50 transition-colors"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Action Buttons */}
            <div className="flex items-center justify-end gap-2.5 pt-3 border-t border-slate-100">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => window.history.back()}
                disabled={loading}
              >
                {language_data?.[authState.lang]?.common_cancel || 'Cancel'}
              </Button>
              <Button
                type="submit"
                variant="default"
                size="sm"
                disabled={loading}
                className="gap-1.5"
              >
                {loading ? (
                  <Spinner color="text-white" />
                ) : (
                  <>
                    <Save className="w-4 h-4" />
                    <span>{language_data?.[authState.lang]?.common_save || 'Link Variant'}</span>
                  </>
                )}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </Fragment>
  );
};

export default AddVariant;